import React from "react";
import { TopFrame } from "@/components/dashboard/TopFrame";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProductTab } from "@/components/marketing/tabs/ProductTab";
import { PriceTab } from "@/components/marketing/tabs/PriceTab";
import { PlaceTab } from "@/components/marketing/tabs/PlaceTab";
import { PromotionTab } from "@/components/marketing/tabs/PromotionTab";
import { SentimentAnalysisTab } from "@/components/marketing/tabs/SentimentAnalysisTab";
import { ContentCreationTab } from "@/components/marketing/tabs/ContentCreationTab";
import { BudgetingTab } from "@/components/marketing/tabs/BudgetingTab";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useMarketingTabs } from "@/hooks/useMarketingTabs";

const Marketing: React.FC = () => {
  const [user, setUser] = React.useState<User | null>(null);
  const { config: marketingTabsConfig, isLoading: tabsLoading } = useMarketingTabs();
  const [pricingApproach, setPricingApproach] = React.useState<string>("cost-plus");
  const [pricingDescription, setPricingDescription] = React.useState<string>("Select a pricing approach to see its description and recommendations.");
  const [address, setAddress] = React.useState('');
  const [shouldAnalyze, setShouldAnalyze] = React.useState(false);
  const [searchTrigger, setSearchTrigger] = React.useState(0);
  const [parties, setParties] = React.useState<Array<{
    type: string;
    name: string;
    address: string;
  }>>([]);
  const [isAddPartyOpen, setIsAddPartyOpen] = React.useState(false);
  const [aiEngine, setAiEngine] = React.useState('openai');
  
  React.useEffect(() => {
    const getUser = async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);
  
  return <div className="min-h-screen bg-[#1A1F2C]">
      <TopFrame user={user} hideProgress />
      <main className="container mx-auto px-4 min-h-[calc(100vh-64px)] bg-[#1A1F2C] py-[15px]">
        <Tabs defaultValue={marketingTabsConfig.budgeting ? "budgeting" : marketingTabsConfig.ai_content_creation ? "content-creation" : marketingTabsConfig.ai_sentiment_analysis ? "sentiment-analysis" : "product"} className="mb-4">
          <TabsList className="w-full justify-center bg-secondary/50 border border-primary/20">
            {marketingTabsConfig.budgeting && (
              <TabsTrigger value="budgeting" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Budgeting
              </TabsTrigger>
            )}
            {marketingTabsConfig.ai_content_creation && (
              <TabsTrigger value="content-creation" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">AI Content Creation</TabsTrigger>
            )}
            {marketingTabsConfig.ai_sentiment_analysis && (
              <TabsTrigger value="sentiment-analysis" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">AI Sentiment Analysis</TabsTrigger>
            )}
            <TabsTrigger value="product" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
              Product
            </TabsTrigger>
            <TabsTrigger value="price" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
              Price
            </TabsTrigger>
            {marketingTabsConfig.place && (
              <TabsTrigger value="place" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Place
              </TabsTrigger>
            )}
            <TabsTrigger value="promotion" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
              Promotion
            </TabsTrigger>
          </TabsList>

          {marketingTabsConfig.budgeting && (
            <TabsContent value="budgeting" className="mt-4">
              <BudgetingTab />
            </TabsContent>
          )}

          {marketingTabsConfig.ai_content_creation && (
            <TabsContent value="content-creation" className="mt-4">
              <ContentCreationTab />
            </TabsContent>
          )}

          {marketingTabsConfig.ai_sentiment_analysis && (
            <TabsContent value="sentiment-analysis" className="mt-4">
              <SentimentAnalysisTab />
            </TabsContent>
          )}

          <TabsContent value="product" className="mt-4">
            <ProductTab />
          </TabsContent>

          <TabsContent value="price" className="mt-4">
            <PriceTab pricingApproach={pricingApproach} setPricingApproach={setPricingApproach} pricingDescription={pricingDescription} setPricingDescription={setPricingDescription} />
          </TabsContent>

          {marketingTabsConfig.place && (
            <TabsContent value="place" className="mt-4">
              <PlaceTab address={address} setAddress={setAddress} shouldAnalyze={shouldAnalyze} setShouldAnalyze={setShouldAnalyze} searchTrigger={searchTrigger} setSearchTrigger={setSearchTrigger} parties={parties} setParties={setParties} isAddPartyOpen={isAddPartyOpen} setIsAddPartyOpen={setIsAddPartyOpen} calculateEmptyRows={(items, minRows = 10) => Array(Math.max(minRows - items.length, 0)).fill(null)} aiEngine={aiEngine} setAiEngine={setAiEngine} />
            </TabsContent>
          )}

          <TabsContent value="promotion" className="mt-4">
            <PromotionTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>;
};

export default Marketing;
